import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Play, LogOut, Lock, Clock, User } from "lucide-react";
import { LOGO_URL } from "../config";
import { useLang } from "../i18n";
import { custHeaders } from "../siteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

export default function AccountPage() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const buyCourse = params.get("buy");
  const [token, setToken] = useState(localStorage.getItem("themaya_customer_token") || "");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [grants, setGrants] = useState(null);

  const startCheckout = (courseId) => {
    window.location.href = `/course/${courseId}`;
  };

  useEffect(() => {
    if (!token) return;
    if (buyCourse) {
      startCheckout(buyCourse);
      return;
    }
    axios
      .get(`${API}/my-courses`, { headers: custHeaders() })
      .then((res) => setGrants(res.data))
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("themaya_customer_token");
          setToken("");
        }
        // transient network/server errors must NOT log the customer out
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "link") {
        await axios.post(`${API}/auth/customer/login-link`, { email: form.email, origin_url: window.location.origin });
        toast.success(ar ? "إذا كان لديك وصول فعّال، سيصلك رابط الدخول على بريدك خلال دقائق" : "If you have active access, a login link will reach your inbox within minutes");
        return;
      }
      if (mode === "forgot") {
        await axios.post(`${API}/auth/customer/forgot-password`, { email: form.email, origin_url: window.location.origin });
        toast.success(ar ? "إذا كان البريد مسجلاً لدينا، سيصلك رابط إعادة التعيين خلال دقائق" : "If this email is registered, a reset link will reach your inbox within minutes");
        return;
      }
      const url = mode === "login" ? `${API}/auth/customer/login` : `${API}/auth/customer/register`;
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const res = await axios.post(url, payload);
      localStorage.setItem("themaya_customer_token", res.data.access_token);
      setToken(res.data.access_token);
      toast.success(ar ? "أهلاً بك" : "Welcome");
    } catch (err) {
      toast.error(err.response?.data?.detail || (ar ? "حدث خطأ" : "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("themaya_customer_token");
    setToken("");
    setGrants(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4" data-testid="account-auth-page">
        <form onSubmit={submit} className="w-full max-w-md bg-luxury-surface border border-luxury-border p-10 space-y-6" data-testid="account-auth-form">
          <div className="text-center">
            <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-luxury-bone">{ar ? "حسابك" : "Your Account"}</h1>
            <p className="text-luxury-gray text-sm mt-2">
              {ar ? "سجّل/سجّلي الدخول للوصول إلى دوراتك" : "Sign in to access your courses"}
            </p>
          </div>
          <div className="flex border border-luxury-border rounded-full overflow-hidden">
            <button type="button" data-testid="account-mode-login" onClick={() => setMode("login")}
              className={`flex-1 py-2.5 text-sm transition-colors ${mode === "login" ? "bg-luxury-gold text-luxury-black font-semibold" : "text-luxury-gray"}`}>
              {ar ? "تسجيل دخول" : "Sign in"}
            </button>
            <button type="button" data-testid="account-mode-register" onClick={() => setMode("register")}
              className={`flex-1 py-2.5 text-sm transition-colors ${mode === "register" ? "bg-luxury-gold text-luxury-black font-semibold" : "text-luxury-gray"}`}>
              {ar ? "حساب جديد" : "Register"}
            </button>
            <button type="button" data-testid="account-mode-link" onClick={() => setMode("link")}
              className={`flex-1 py-2.5 text-sm transition-colors ${mode === "link" ? "bg-luxury-gold text-luxury-black font-semibold" : "text-luxury-gray"}`}>
              {ar ? "دخول عبر الإيميل" : "Email link"}
            </button>
          </div>
          {mode === "link" && (
            <p className="text-luxury-gray text-xs text-center">
              {ar ? "بدون كلمة مرور — سيصلك رابط دخول شخصي على بريدك إذا كان لديك كورس مفعّل" : "No password — you'll receive a personal login link if you have an active course"}
            </p>
          )}
          {mode === "forgot" && (
            <p className="text-luxury-gray text-xs text-center">
              {ar ? "أدخلي بريدك وسنرسل لك رابطاً لاختيار كلمة مرور جديدة — يعمل لمدة ساعة واحدة" : "Enter your email and we'll send a link to choose a new password — valid for one hour"}
            </p>
          )}
          {mode === "register" && (
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{ar ? "الاسم" : "Name"}</label>
              <input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
            </div>
          )}
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{ar ? "البريد الإلكتروني" : "Email"}</label>
            <input data-testid="account-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
          </div>
          {(mode === "login" || mode === "register") && (
            <div>
              <label className="block text-sm text-luxury-gray mb-2">{ar ? "كلمة المرور" : "Password"}</label>
              <input data-testid="account-password-input" type="password" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} required minLength={8} />
              {mode === "login" && (
                <button type="button" data-testid="account-forgot-link" onClick={() => setMode("forgot")}
                  className="mt-2 text-xs text-luxury-gray hover:text-luxury-gold transition-colors">
                  {ar ? "نسيتِ كلمة المرور؟" : "Forgot your password?"}
                </button>
              )}
            </div>
          )}
          <button data-testid="account-submit-button" type="submit" disabled={loading}
            className="w-full bg-luxury-gold text-luxury-black font-semibold py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "link" ? (ar ? "إرسال رابط الدخول" : "Send login link") : mode === "forgot" ? (ar ? "إرسال رابط إعادة التعيين" : "Send reset link") : mode === "login" ? (ar ? "دخول" : "Sign in") : (ar ? "إنشاء الحساب" : "Create account")}
          </button>
          <Link to="/" data-testid="account-back-home" className="block text-center text-luxury-gray text-sm hover:text-luxury-gold transition-colors">
            {ar ? "العودة إلى الصفحة الرئيسية" : "Back to home"}
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-black py-28 px-4 md:px-8" data-testid="account-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="TheMaya" className="h-8" />
            <h1 className="font-serif text-3xl md:text-4xl text-luxury-bone">{ar ? "مكتبتي" : "My Library"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-luxury-gray hover:text-luxury-gold text-sm transition-colors">{ar ? "الرئيسية" : "Home"}</Link>
            <button data-testid="account-logout-button" onClick={logout} className="flex items-center gap-2 text-luxury-gray hover:text-red-400 text-sm transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              {ar ? "خروج" : "Sign out"}
            </button>
          </div>
        </div>

        {grants === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>
        ) : grants.length === 0 ? (
          <div className="text-center py-20 border border-luxury-border bg-luxury-surface" data-testid="account-empty">
            <Lock className="w-10 h-10 mx-auto mb-4 text-luxury-gold" strokeWidth={1} />
            <p className="text-luxury-gray">{ar ? "لا تملك/تملكين دورات بعد — تصفّح/تصفّحي الدورات وابدأ/ابدئي رحلتك" : "No courses yet — browse the courses and start your journey"}</p>
            <Link to="/#courses" data-testid="account-browse-courses" className="inline-block mt-6 bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors">
              {ar ? "تصفح الدورات" : "Browse courses"}
            </Link>
          </div>
        ) : (
          <div className="space-y-5">
            {grants.map((g, i) => (
              <div key={i} data-testid={`account-course-${i}`} className="bg-luxury-surface border border-luxury-border p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-luxury-bone font-serif text-2xl">{g.course ? (ar ? g.course.title : (g.course.title_en || g.course.title)) : g.course_id}</p>
                  <p className="text-luxury-gray text-sm mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                    {g.expires_at
                      ? (ar ? `الوصول حتى ${g.expires_at.slice(0, 10)}` : `Access until ${g.expires_at.slice(0, 10)}`)
                      : (ar ? "وصول مدى الحياة" : "Lifetime access")}
                    {!g.active && <span className="text-red-400">{ar ? " — انتهت الصلاحية" : " — expired"}</span>}
                  </p>
                  <p className="text-luxury-gray text-xs mt-1">{g.videos.length} {ar ? "فيديو" : "videos"}</p>
                </div>
                {g.active && (
                  <Link to={`/watch/${g.course_id}`} data-testid={`account-watch-${i}`}
                    className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors">
                    <Play className="w-4 h-4" strokeWidth={1.5} />
                    {ar ? "شاهد/شاهدي الآن" : "Watch now"}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-8 text-center">
          <Link to="/#courses" className="text-luxury-gray text-sm hover:text-luxury-gold transition-colors flex items-center justify-center gap-2">
            <User className="w-4 h-4" strokeWidth={1.5} />
            {ar ? "شراء دورة جديدة" : "Buy another course"}
          </Link>
        </div>
      </div>
    </div>
  );
}
